import { idbStorageEffect } from './../utils/persistence';
import { atom, atomFamily } from 'recoil';
import { selector, selectorFamily } from './recoilSelectors';
import { hasMovingSpeed, Member } from '../types/member';
import { atomWithPersistence } from '../utils/persistence';
import { sortMembers } from "../utils/memberUtils";
import { Uid, UidNameUsername } from '../types/core';
import { containsSubstring } from "../utils/filter";
import { pageState } from './appState';
import { PAGE } from '../constants/pages';
import { drawModeState } from './drawModeState';
import equal from "fast-deep-equal/react";
import { getRecoil, setRecoil } from "../providers/RecoilAccessProvider";
import { routingHoverState } from "./viewState";
import { boardMemberUidsState } from "./boardState";
import { showMembersLayerState } from "./mapState";
import { memberPassesPeopleFiltersState, peopleFilterScopeForPageState } from "./peopleFiltersState";


export const memberUidsState = atom<Uid[]>({
    key: "members.uids",
    default: [] as Uid[],
    effects: [idbStorageEffect]
});

export const pinnedMemberUidsState = atomWithPersistence<Uid[]>("members.uids.pinned", []);

export const memberState = atomFamily<Member | undefined, Uid>({
    key: 'member',
    default: undefined,
    effects: [idbStorageEffect]
});

/**
 * A person's lunch break as "1200-1300", or "" for none: a string, so the route's times don't follow every other
 * change to the person, such as each new position.
 */
export const memberLunchState = selectorFamily<string, Uid>({
    key: 'member.lunch',
    get: (uid) => ({ get }) => {
        const lunch = get(memberState(uid))?.lunch;
        return lunch ? lunch.start + "-" + lunch.end : "";
    },
});

export function updateSingleMemberState(member: Member) {
    updateMultiMemberState([member]);
}

function normalizeMember(member: Member): Member {
    return {
        ...member,
        teams: member.teams ?? [],
    };
}

export function updateMultiMemberState(members: Member[]) {
    members.forEach(member => {
        const normalized = normalizeMember(member);
        const existing = getRecoil(memberState(normalized.uid));
        normalized.lastMovingTs = resolveLastMovingTs(normalized, existing);
        // Periodic refreshes re-deliver the whole roster; writing an unchanged
        // member would still notify every subscriber and hit the idb effect.
        if (equal(normalized, existing)) {
            return;
        }
        setRecoil(memberState(normalized.uid), normalized);
    });
}

function resolveLastMovingTs(next: Member, previous: Member | undefined): number | undefined {
    if (hasMovingSpeed(next)) {
        return next.location.ts;
    }
    if (previous?.lastMovingTs) {
        return previous.lastMovingTs;
    }
    return previous && hasMovingSpeed(previous) ? previous.location.ts : undefined;
}

export const membersDangerousState = selector<Member[]>({
    key: "members.all",
    get: ({ get }) => {
        const members = get(memberUidsState)
            .map(uid => get(memberState(uid)))
            .filter(member => !!member) as Member[];
        return members
            .filter(member => member.username)
            .sort(sortMembers);
    }
});

// use this to avoid unnecessary updates if you are only interested in member names
// it is updated in RecoilCacheProvider
export const memberNamesState = atom({
    key: "member.names",
    default: [] as UidNameUsername[],
});

/**
 * Whether a person shows in the list and on the map: the people search everywhere, and the people
 * filters on the pages that have a filter bar for them.
 */
export const memberIsVisibleState = selectorFamily<boolean, Uid>({
    key: "member.name.visibility",
    get: (uid) => ({ get }) => {
        const search = get(memberSearchState);
        if (search.length > 0) {
            const member = get(memberState(uid));
            const searchLowerCase = search.toLowerCase();
            if (!containsSubstring(member?.name.toLowerCase(), searchLowerCase)
                && !containsSubstring(member?.username, searchLowerCase)) {
                return false;
            }
        }
        const scope = get(peopleFilterScopeForPageState);
        return !scope || get(memberPassesPeopleFiltersState({ uid, scope }));
    }
});

export const memberNamesFilteredState = selector({
    key: "members.names.filtered",
    get: ({ get }) => {
        const memberNames = get(memberNamesState);
        return memberNames.filter((memberName) => get(memberIsVisibleState(memberName.uid)));
    }
});

// the same people the list and the map show, so a box selection never picks someone hidden
export const membersFilteredDangerousState = selector({
    key: "members.filtered",
    get: ({ get }) => {
        const members = get(membersDangerousState);
        return members.filter(member => get(memberIsVisibleState(member.uid)));
    },
});

export const memberSearchState = atom({
    key: "members.filter.search",
    default: "",
});

export const updateTeamMembersState = atom<number>({
    key: "team.members.update",
    default: 0,
});

export const memberUidsOnMap = selector({
    key: "members.onmap.uids",
    get: ({ get }) => {
        const routingHover = get(routingHoverState);
        if (routingHover) {
            const uid = JSON.parse(routingHover).uid;
            if (uid) {
                return [uid as Uid];
            }
        }
        const page = get(pageState);
        if (page === PAGE.HEATMAP || get(drawModeState)) {
            return [];
        } else {
            const memberUids = get(memberUidsState);
            const boardMembers = get(boardMemberUidsState);
            const show = get(showMembersLayerState);
            return boardMembers.length > 0 ? boardMembers : show ? memberUids : [];
        }
    }
})
